/**
 * TEMP-H-002: Execution Control Bar
 * 
 * Main control interface for pause/resume/cancel operations.
 * Provides optimistic UX with permission gating and confirmation dialogs.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Play, 
  Pause, 
  Square, 
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { useControlQueries } from '@/hooks/useControlQueries';
import { useControlSignals } from '@/hooks/useControlSignals';
import { useServerAuth } from '@/hooks/useAuth-cookie';
import { cn } from '@/utils/cn';
import type { ExecutionStatusType } from '@/types/execution';

interface ExecutionControlBarProps {
  executionId: string;
  executionStatus: ExecutionStatusType;
  className?: string;
  onStateChange?: (newState: ExecutionStatusType) => void;
}

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText: string;
  confirmVariant: 'danger' | 'warning';
  isLoading?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  confirmVariant,
  isLoading = false
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-slate-800 border border-slate-700 rounded-lg p-6 max-w-md w-full"
      >
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-slate-200 mb-2">{title}</h3>
            <p className="text-slate-400 text-sm leading-relaxed">{message}</p>
          </div>
        </div>
        
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 disabled:opacity-50',
              confirmVariant === 'danger'
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-amber-600 hover:bg-amber-700 text-white'
            )}
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {confirmText}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const ExecutionControlBar: React.FC<ExecutionControlBarProps> = ({
  executionId,
  executionStatus,
  className,
  onStateChange
}) => {
  const { isAdmin } = useServerAuth();
  const { data: queries, isLoading: queriesLoading } = useControlQueries(executionId);
  const signals = useControlSignals(executionId, {
    onSuccess: (response) => {
      // Optimistically update parent state
      if (response.signalType === 'pause') {
        onStateChange?.('paused');
      } else if (response.signalType === 'resume') {
        onStateChange?.('running');
      } else if (response.signalType === 'cancel') {
        onStateChange?.('cancelled');
      }
    }
  });

  const [confirmDialog, setConfirmDialog] = useState<{
    type: 'pause' | 'cancel' | null;
    isOpen: boolean;
  }>({ type: null, isOpen: false });

  // Permission checks
  const canControl = isAdmin && queries?.canControl;
  const isTerminalState = ['completed', 'failed', 'cancelled'].includes(executionStatus);
  const isRunning = executionStatus === 'running';
  const isPaused = executionStatus === 'paused';

  // Handle control actions
  const handlePause = async () => {
    if (!canControl || !isRunning) return;
    
    setConfirmDialog({ type: 'pause', isOpen: true });
  };

  const handleResume = async () => {
    if (!canControl || !isPaused) return;
    
    try {
      await signals.resumeExecution({
        reason: 'Manual resume via control bar'
      });
    } catch (error) {
      // Error handled by signals hook
    }
  };

  const handleCancel = async () => {
    if (!canControl || isTerminalState) return;
    
    setConfirmDialog({ type: 'cancel', isOpen: true });
  };

  const confirmAction = async () => {
    try {
      if (confirmDialog.type === 'pause') {
        await signals.pauseExecution({
          reason: 'Manual pause via control bar'
        });
      } else if (confirmDialog.type === 'cancel') {
        await signals.cancelExecution({
          reason: 'Manual cancellation via control bar'
        });
      }
    } catch (error) {
      // Error handled by signals hook
    } finally {
      setConfirmDialog({ type: null, isOpen: false });
    }
  };

  // Show loading state while queries are loading
  if (queriesLoading || !queries) {
    return (
      <div className={cn('flex items-center gap-3 p-4 bg-slate-800/30 border border-slate-700/30 rounded-lg', className)}>
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        <span className="text-slate-400 text-sm">Loading controls...</span>
      </div>
    );
  }

  // Don't show controls for non-admins or when permission is denied
  if (!canControl) {
    return (
      <div className={cn('flex items-center gap-3 p-4 bg-slate-800/30 border border-slate-700/30 rounded-lg opacity-50', className)}>
        <span className="text-slate-500 text-sm">
          {!isAdmin ? 'Admin access required' : 'Controls unavailable'}
        </span>
      </div>
    );
  }

  return (
    <>
      <div className={cn(
        'flex items-center gap-3 p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg backdrop-blur-sm',
        className
      )}>
        {/* Status Indicator */}
        <div className="flex items-center gap-2">
          <div className={cn(
            'w-2 h-2 rounded-full',
            isRunning ? 'bg-emerald-400 animate-pulse' :
            isPaused ? 'bg-amber-400' :
            isTerminalState ? 'bg-slate-400' : 'bg-slate-600'
          )} />
          <span className="text-slate-300 text-sm font-medium capitalize">
            {executionStatus}
          </span>
        </div>

        <div className="h-4 w-px bg-slate-600 mx-2" />

        {/* Control Buttons */}
        <div className="flex items-center gap-2">
          {/* Pause/Resume Toggle */}
          {!isTerminalState && (
            <motion.button
              onClick={isRunning ? handlePause : handleResume}
              disabled={signals.isLoading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all',
                'border border-slate-600/50 hover:border-slate-500/50',
                isRunning 
                  ? 'bg-amber-600/20 text-amber-300 hover:bg-amber-600/30' 
                  : 'bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/30',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {signals.isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isRunning ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              {isRunning ? 'Pause' : 'Resume'}
            </motion.button>
          )}

          {/* Cancel Button */}
          {!isTerminalState && (
            <motion.button
              onClick={handleCancel}
              disabled={signals.isLoading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all bg-red-600/20 text-red-300 hover:bg-red-600/30 border border-red-600/50 hover:border-red-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {signals.isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Square className="w-4 h-4" />
              )}
              Cancel
            </motion.button>
          )}
        </div>
      </div>

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen && confirmDialog.type === 'pause'}
        onClose={() => setConfirmDialog({ type: null, isOpen: false })}
        onConfirm={confirmAction}
        title="Pause Execution"
        message="This will pause the current execution. You can resume it later from the same point."
        confirmText="Pause Execution"
        confirmVariant="warning"
        isLoading={signals.isLoading}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen && confirmDialog.type === 'cancel'}
        onClose={() => setConfirmDialog({ type: null, isOpen: false })}
        onConfirm={confirmAction}
        title="Cancel Execution"
        message="This will permanently cancel the execution. This action cannot be undone and any progress will be lost."
        confirmText="Cancel Execution"
        confirmVariant="danger"
        isLoading={signals.isLoading}
      />
    </>
  );
};

export default ExecutionControlBar;